<template>
	<Modal
		:title="$t('seller.upload.installmentContract.autoRates.modal.title')"
		:close-on-click-outside="false"
		@close="closeModal"
		:width="700"
	>
		<template v-slot:body>
			<div class="modal-content text_left">
				<span class="text_left" v-html="$t('seller.upload.installmentContract.autoRates.modal.description')"></span>
				<div class="flex flex_gap margin_top">
					<md-field class="flex_1" :class="[{ 'md-error': errors.has('grossAmount') }]">
						<label>{{ $t("seller.upload.installmentContract.autoRates.modal.grossAmountPerRate") }}</label>
						<MoneyAmountInput
							v-model="form.grossAmount"
							data-vv-name="grossAmount"
							v-validate="modelValidations.grossAmount"
							:required="modelValidations.grossAmount.required"
						/>
						<span v-if="errors.has('grossAmount')" class="validation-error">{{ errors.first('grossAmount') }}</span>
					</md-field>
					<md-field class="flex_1" :class="[{ 'md-error': errors.has('numberOfRates') }]">
						<label>{{ $t("seller.upload.installmentContract.autoRates.modal.noOfRegularRates") }}</label>
						<MoneyAmountInput
							v-model="form.numberOfRates" :decimals="0"
							data-vv-name="numberOfRates"
							v-validate="modelValidations.numberOfRates"
							:required="modelValidations.numberOfRates.required"
						/>
						<span v-if="errors.has('numberOfRates')" class="validation-error">{{ errors.first('numberOfRates') }}</span>
					</md-field>
				</div>

				<div class="flex flex_gap">
					<DatePicker
						class="flex_1"
						v-model="form.firstDueDate"
						:placeholder="$t('seller.upload.installmentContract.autoRates.modal.firstDueDate')"
						@input="onDueDateChanged"
						data-vv-name="firstDueDate"
						v-validate="modelValidations.firstDueDate"
						:required="modelValidations.firstDueDate.required"
						:error="errors.first('firstDueDate')"
					/>
					<EnumSelect
						class="flex_1"
						:label="$t('seller.upload.installmentContract.autoRates.modal.paymentInterval')"
						v-model="form.interval"
						:enumDef="enumDef.installmentContractRatePaymentInterval"
						:filtered-values="allowedRateIntervalOptions"
						data-vv-name="interval"
						v-validate="modelValidations.interval"
						:required="modelValidations.interval.required"
						:error="errors.first('interval')"
					/>
				</div>

				<div class="flex flex_between margin_top">
					<md-button class="md-blank" @click="closeModal">
						{{ $t("common.button.close") }}
					</md-button>
					<md-button :class="{'md-primary': isFormValid }" @click="generateAutoRates">
						<i class="fa fa-magic" aria-hidden="true"></i>&nbsp;
						{{ $t("seller.upload.installmentContract.autoRates.generateButton") }}
					</md-button>
				</div>
			</div>
		</template>
	</Modal>
</template>

<script>
import Modal from "@/components/templates/modal/Modal.vue";
import DatePicker from "@/components/templates/DatePicker.vue";
import EnumSelect from "@/components/templates/select/EnumSelect.vue";
import MoneyAmountInput from "@/components/templates/common/MoneyAmountInput.vue";
import {enumDef} from "@/common/enumDefs";
import {validateForm, validateRequiredFields} from "@/validation/validation";

export default {
	name: "InstallmentContractAutoGenerateRatesModal",
	components: { Modal, DatePicker, EnumSelect, MoneyAmountInput },
	data() {
		return {
			enumDef,
			form: {
				grossAmount: null,
				numberOfRates: null,
				firstDueDate: "",
				interval: enumDef.installmentContractRatePaymentInterval.every1MonthsSameDay
			},
			modelValidations: {
				grossAmount: {
					required: true
				},
				numberOfRates: {
					required: true
				},
				firstDueDate: {
					required: true
				},
				interval: {
					required: true
				}
			}
		};
	},
	computed: {
		isFormValid() {
			return validateRequiredFields(this.form, this.modelValidations);
		},
		areSameDayOptionsAllowed() {
			return !this.form.firstDueDate || moment(this.form.firstDueDate).date() <= 28;
		},
		allowedRateIntervalOptions() {
			return [
				...(this.areSameDayOptionsAllowed ? enumDef.installmentContractRatePaymentInterval.sameDayOptions() : []),
				...enumDef.installmentContractRatePaymentInterval.endOfMonthOptions(),
			]
		}
	},
	methods: {
		onDueDateChanged() {
			// if a same-day option is selected but no longer supported by the new due date, we need to reset the interval.
			// Otherwise, the user might end up with a rate interval not supported by the system.
			if (enumDef.installmentContractRatePaymentInterval.isSameDay(this.form.interval) && !this.areSameDayOptionsAllowed) {
				this.form.interval = null;
			}
		},
		closeModal() {
			this.$emit("close");
		},
		generateAutoRates() {
			validateForm(this, this.$validator)
				.then(() => {
					const stepMonths = 1; // Currently, we support no options other than monthly

					const receivables = [];
					for (let i = 0; i < this.form.numberOfRates; i++) {
						let nextDueDate = moment(this.form.firstDueDate).add(i * stepMonths, "months");

						// Adjust by interval option. Same-day option needs no special handling and is the default
						if (enumDef.installmentContractRatePaymentInterval.isEndOfMonth(this.form.interval)) {
							nextDueDate.endOf("month");
						}

						receivables.push({
							amount: this.form.grossAmount,
							dueDate: nextDueDate.format("YYYY-MM-DD")
						});
					}

					this.$emit("generated", receivables);
				})
				.catch(this.toast);
		}
	}
};
</script>
